import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Campo de data de nascimento que o cliente DIGITA (sem calendário nativo): vai
// mascarando como DD/MM/AAAA enquanto digita e emite ISO YYYY-MM-DD pro servidor
// (que é o formato que o backend/coluna `date` esperam). Emite "" enquanto a
// data está incompleta ou inválida, então a validação de "obrigatório" do
// chamador continua funcionando.

// "1990-05-23" -> "23/05/1990" (vazio se não for ISO completo).
function isoToBr(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

// Mascara o que foi digitado como DD/MM/AAAA (só dígitos, no máximo 8).
function maskBr(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// "23/05/1990" -> "1990-05-23" se for data real, no passado e ano >= 1900; senão "".
function brToIso(br: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br);
  if (!m) return "";
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (yyyy < 1900 || mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";
  const date = new Date(yyyy, mm - 1, dd);
  // Rejeita datas que "estouram" (ex: 31/02 vira 03/03).
  if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date.getTime() > today.getTime()) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

interface BirthDateInputProps {
  id?: string;
  value: string; // ISO YYYY-MM-DD ou ""
  onChange: (iso: string) => void; // ISO quando completa/válida, "" caso contrário
  className?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}

export function BirthDateInput({ id, value, onChange, className, autoFocus, onEnter }: BirthDateInputProps) {
  const [display, setDisplay] = useState(() => isoToBr(value));

  // Sincroniza o display quando o ISO muda por fora (ex: prefill) e não bate com
  // o que já está digitado — a checagem evita resetar quando o próprio onChange
  // acabou de propagar o valor digitado.
  useEffect(() => {
    if ((brToIso(display) || "") !== value) {
      setDisplay(value ? isoToBr(value) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const complete = display.replace(/\D/g, "").length === 8;
  const invalid = complete && !brToIso(display);

  return (
    <div className="space-y-1">
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="DD/MM/AAAA"
        maxLength={10}
        value={display}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        className={cn(invalid && "border-destructive focus-visible:ring-destructive", className)}
        onChange={(e) => {
          const masked = maskBr(e.target.value);
          setDisplay(masked);
          onChange(brToIso(masked));
        }}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
      />
      {invalid && <p className="text-xs text-destructive">Data inválida. Use DD/MM/AAAA.</p>}
    </div>
  );
}
